import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Pin, Filter, Loader2, Plane, MapPin, Users, ChevronRight, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NoteCard } from '@/components/notes/NoteCard';
import { notesApi } from '@/services/notesApi';
import { useToast } from '@/components/ui/use-toast';
import type { NoteListItem, RouteGroup, AirportGroup, CrewGroup, NoteFilterOptions } from '@/types/notes.types';

type ViewMode = 'all' | 'route' | 'airport' | 'crew';

const VIEW_OPTIONS: { key: ViewMode; label: string; icon: typeof List }[] = [
  { key: 'all', label: '全部', icon: List },
  { key: 'route', label: '按航线', icon: Plane },
  { key: 'airport', label: '按机场', icon: MapPin },
  { key: 'crew', label: '按机组', icon: Users },
];

type SortBy = 'updatedAt' | 'createdAt' | 'title';

const SORT_OPTIONS: { key: SortBy; label: string }[] = [
  { key: 'updatedAt', label: '最近更新' },
  { key: 'createdAt', label: '最近创建' },
  { key: 'title', label: '标题' },
];

export default function NotesListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // 视图模式
  const [viewMode, setViewMode] = useState<ViewMode>('all');

  // 全部视图状态
  const [notes, setNotes] = useState<NoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>('updatedAt');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // 筛选器
  const [filterOptions, setFilterOptions] = useState<NoteFilterOptions>({ tags: [], routes: [], airports: [], crewMembers: [] });
  const [routeFilter, setRouteFilter] = useState<number | ''>('');
  const [airportFilter, setAirportFilter] = useState<string | ''>('');
  const [crewFilter, setCrewFilter] = useState<number | ''>('');
  const [tagFilter, setTagFilter] = useState<string | ''>('');

  // 分组视图状态
  const [routeGroups, setRouteGroups] = useState<RouteGroup[]>([]);
  const [airportGroups, setAirportGroups] = useState<AirportGroup[]>([]);
  const [crewGroups, setCrewGroups] = useState<CrewGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // 加载筛选选项
  useEffect(() => {
    notesApi.getFilterOptions()
      .then((res) => setFilterOptions(res.data.data))
      .catch(() => toast({ title: '加载筛选项失败', variant: 'destructive' }));
  }, [toast]);

  // 加载全部视图笔记
  const fetchNotes = useCallback(async (pageNum = 1, append = false) => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page: pageNum, pageSize: 20, sortBy };
      if (search) params.search = search;
      if (showPinnedOnly) params.sortBy = 'updatedAt';
      if (routeFilter) params.route = routeFilter;
      if (airportFilter) params.airport = airportFilter;
      if (crewFilter) params.crew = crewFilter;
      if (tagFilter) params.tag = tagFilter;

      const res = await notesApi.getNotes(params);
      const results = res.data.results || [];
      const count = res.data.count || 0;

      if (append) {
        setNotes(prev => [...prev, ...results]);
      } else {
        setNotes(results);
      }
      setHasMore(results.length === 20 && (append ? notes.length + results.length : results.length) < count);
    } catch (err) {
      toast({ title: '获取笔记失败', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, showPinnedOnly, sortBy, routeFilter, airportFilter, crewFilter, tagFilter, toast]);

  // 加载分组视图
  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      if (viewMode === 'route') {
        const res = await notesApi.getByRoute();
        setRouteGroups(res.data.data || []);
      } else if (viewMode === 'airport') {
        const res = await notesApi.getByAirport();
        setAirportGroups(res.data.data || []);
      } else if (viewMode === 'crew') {
        const res = await notesApi.getByCrew();
        setCrewGroups(res.data.data || []);
      }
    } catch (err) {
      toast({ title: '获取分组数据失败', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [viewMode, toast]);

  // 视图/筛选变化时重置并加载
  useEffect(() => {
    setPage(1);
    setExpandedGroups(new Set());
    if (viewMode === 'all') {
      fetchNotes(1, false);
    } else {
      fetchGroups();
    }
  }, [viewMode, fetchNotes, fetchGroups]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchNotes(next, true);
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // 全部视图：分离置顶和非置顶
  const { pinned, unpinned } = useMemo(() => ({
    pinned: notes.filter(n => n.isPinned),
    unpinned: notes.filter(n => !n.isPinned),
  }), [notes]);

  // 全部视图：是否有任何筛选激活
  const hasActiveFilters = routeFilter || airportFilter || crewFilter || tagFilter;

  // 清除全部筛选
  const clearFilters = () => {
    setRouteFilter('');
    setAirportFilter('');
    setCrewFilter('');
    setTagFilter('');
    setSearch('');
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* 页面标题 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">全部笔记</h1>
          <p className="text-sm text-gray-500 mt-1">
            {viewMode === 'all' ? `共 ${notes.length} 篇笔记` : '按维度分组浏览笔记'}
          </p>
        </div>
        <Button onClick={() => navigate('/notes/new')} className="gap-2">
          <Plus className="w-4 h-4" />
          新建笔记
        </Button>
      </div>

      {/* 视图切换 */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {VIEW_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          return (
            <button
              key={opt.key}
              onClick={() => setViewMode(opt.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === opt.key
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* 全部视图：搜索 + 筛选 */}
      {viewMode === 'all' && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 space-y-3">
          {/* 搜索 */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索笔记标题、内容、标签..."
                className="pl-9"
              />
            </div>
            <button
              onClick={() => setShowPinnedOnly(!showPinnedOnly)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                showPinnedOnly ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Pin className="w-4 h-4" />
              置顶
            </button>
          </div>

          {/* 维度筛选 */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={routeFilter}
              onChange={(e) => setRouteFilter(e.target.value ? Number(e.target.value) : '')}
              className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">按航线筛选</option>
              {filterOptions.routes.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>

            <select
              value={airportFilter}
              onChange={(e) => setAirportFilter(e.target.value)}
              className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">按机场筛选</option>
              {filterOptions.airports.map(ap => (
                <option key={ap.iataCode} value={ap.iataCode}>{ap.iataCode} {ap.name}</option>
              ))}
            </select>

            <select
              value={crewFilter}
              onChange={(e) => setCrewFilter(e.target.value ? Number(e.target.value) : '')}
              className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">按机组筛选</option>
              {filterOptions.crewMembers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">按标签筛选</option>
              {filterOptions.tags.map(tag => (
                <option key={tag} value={tag}>#{tag}</option>
              ))}
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                清除筛选
              </button>
            )}
          </div>

          {/* 排序 */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">排序:</span>
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  sortBy === opt.key
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 全部视图：笔记列表 */}
      {viewMode === 'all' && (
        <>
          {loading && notes.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <Filter className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">暂无笔记</h3>
              <p className="text-sm text-gray-500 mb-4">
                {hasActiveFilters ? '当前筛选条件下没有找到笔记' : '点击右上角按钮创建第一篇笔记'}
              </p>
              {hasActiveFilters ? (
                <Button onClick={clearFilters} variant="outline">清除筛选</Button>
              ) : (
                <Button onClick={() => navigate('/notes/new')} variant="outline">
                  <Plus className="w-4 h-4 mr-1" />
                  新建笔记
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* 置顶区域 */}
              {pinned.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">置顶</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pinned.map(note => (
                      <NoteCard key={note.id} note={note} />
                    ))}
                  </div>
                </div>
              )}

              {/* 非置顶区域 */}
              {unpinned.length > 0 && (
                <div>
                  {pinned.length > 0 && (
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 pt-4 border-t border-gray-100">
                      其他
                    </h2>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {unpinned.map(note => (
                      <NoteCard key={note.id} note={note} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 加载更多 */}
          {hasMore && (
            <div className="mt-6 text-center">
              <Button variant="outline" onClick={handleLoadMore} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                加载更多
              </Button>
            </div>
          )}
        </>
      )}

      {/* 分组视图：按航线 */}
      {viewMode === 'route' && (
        <GroupView
          loading={loading}
          groups={routeGroups.map(g => ({ key: g.routeCode, name: g.routeName, count: g.count, notes: g.notes }))}
          expanded={expandedGroups}
          onToggle={toggleGroup}
          icon={Plane}
          emptyText="暂无按航线归类的笔记"
        />
      )}

      {/* 分组视图：按机场 */}
      {viewMode === 'airport' && (
        <GroupView
          loading={loading}
          groups={airportGroups.map(g => ({ key: g.iataCode, name: g.name, count: g.count, notes: g.notes }))}
          expanded={expandedGroups}
          onToggle={toggleGroup}
          icon={MapPin}
          emptyText="暂无按机场归类的笔记"
        />
      )}

      {/* 分组视图：按机组 */}
      {viewMode === 'crew' && (
        <GroupView
          loading={loading}
          groups={crewGroups.map(g => ({ key: String(g.id), name: g.name, count: g.count, notes: g.notes }))}
          expanded={expandedGroups}
          onToggle={toggleGroup}
          icon={Users}
          emptyText="暂无按机组归类的笔记"
        />
      )}
    </div>
  );
}

// 分组视图子组件
interface GroupItem {
  key: string;
  name: string;
  count: number;
  notes: NoteListItem[];
}

interface GroupViewProps {
  loading: boolean;
  groups: GroupItem[];
  expanded: Set<string>;
  onToggle: (key: string) => void;
  icon: typeof Plane;
  emptyText: string;
}

function GroupView({ loading, groups, expanded, onToggle, icon: Icon, emptyText }: GroupViewProps) {
  if (loading && groups.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (groups.length === 0) {
    return <div className="text-center py-16 text-gray-500">{emptyText}</div>;
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.key} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => onToggle(group.key)}
            className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors"
          >
            <Icon className="w-5 h-5 text-blue-500" />
            <div className="flex-1 text-left">
              <span className="font-semibold text-gray-900">{group.name}</span>
              <span className="ml-2 text-sm text-gray-500">{group.count} 篇笔记</span>
            </div>
            <ChevronRight
              className={`w-5 h-5 text-gray-400 transition-transform ${
                expanded.has(group.key) ? 'rotate-90' : ''
              }`}
            />
          </button>
          {expanded.has(group.key) && (
            <div className="px-5 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {group.notes.map((note) => (
                <NoteCard key={note.id} note={note} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
